import { Faq, NewFaq } from "@/types/faq";
import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import { addDoc, collection, deleteDoc, doc, getDocs, serverTimestamp, updateDoc } from "firebase/firestore";
import { db } from "@/firebase";
import { RootState } from "@/store/store";
import { addLoadingTask, deleteLoadingTask } from "@/store/uiSlice";
import { pick } from "lodash";

export const faqsApi = createApi({
  reducerPath: "faqs",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["Faq"],
  endpoints: (builder) => ({
    fetchFaqs: builder.query({
      queryFn: async (organizationId: string, { dispatch }) => {
        const TASK = "Preguntas Frecuentes";

        try {
          dispatch(addLoadingTask(TASK));
          const collectionRef = collection(db, `organizations/${organizationId}/faqs`);

          const response = await getDocs(collectionRef).then((querySnapshot) => {
            return querySnapshot.docs.reduce((acc, doc: any) => {
              return [...acc, { ...doc.data(), id: doc.id }];
            }, [] as Faq[]);
          });

          return { data: response };
        } catch (error) {
          return { error };
        } finally {
          dispatch(deleteLoadingTask(TASK));
        }
      },
      providesTags: ["Faq"],
    }),
    addFaq: builder.mutation({
      queryFn: async ({ organizationId, payload }: { organizationId: string; payload: NewFaq }, { getState }) => {
        try {
          const { auth } = getState() as RootState;

          if (!auth?.user?.uid) {
            return { data: undefined };
          }

          const draft: NewFaq = {
            ...payload,
            createdBy: auth.user.uid,
            createdAt: serverTimestamp(),
          };

          const collectionRef = collection(db, `organizations/${organizationId}/faqs`);
          const response = await addDoc(collectionRef, draft);
          return { data: response };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: ["Faq"],
    }),
    updateFaq: builder.mutation({
      queryFn: async (
        { organizationId, faqId, faq }: { organizationId: string; faqId: string; faq: Faq },
        { getState }
      ): Promise<{ data?: unknown; error?: unknown }> => {

        const ALLOWED_FIELDS = [
            "question",
            "answer",
            "lastUpdatedBy",
            "lastUpdatedAt",
          ];

        try {
          const state = getState() as RootState;
          const auth = state.auth;

          if (!auth?.user?.uid) {
            return { data: undefined };
          }

          const collectionRef = doc(db, `organizations/${organizationId}/faqs/${faqId}`);
          const payload: Partial<Faq> = {
            ...pick(faq, ALLOWED_FIELDS),
            lastUpdatedBy: auth.user.uid,
            lastUpdatedAt: serverTimestamp(),
          };
          const response: unknown = await updateDoc(collectionRef, payload);
          return { data: response };

        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: ["Faq"],
    }),
    deleteFaq: builder.mutation({
      queryFn: async ({ organizationId, faqId }: { organizationId: string; faqId: string }) => {
        try {
          const collectionRef = doc(db, `organizations/${organizationId}/faqs/${faqId}`);
          const response = await deleteDoc(collectionRef);
          return { data: response };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: ["Faq"],
    }),
  }),
});

export const { useFetchFaqsQuery, useAddFaqMutation, useDeleteFaqMutation, useUpdateFaqMutation } = faqsApi;
